'use client';

import { use, useMemo } from 'react';

import { ProductCompareContext } from '../context/product-compare-context';

/**
 * Normalize product ID to ensure 'product-' prefix without duplication
 */
const normalizeProductId = (productId: string): string => (productId.startsWith('product-') ? productId : `product-${productId}`);

/**
 * Hook that subscribes only to whether a specific product is selected in the compare list.
 * This prevents unnecessary re-renders when other products are added/removed from the compare list.
 *
 * @param productId - The product ID to check
 * @returns Object containing isChecked state and compare actions
 */
export const useProductCompareSelection = (productId: string) => {
   const context = use(ProductCompareContext);

   const normalizedId = useMemo(() => normalizeProductId(productId), [productId]);

   // Derive only the state needed for this specific product
   const isChecked = useMemo(
      () => context.productItems.some((item) => item.id === normalizedId),
      [context.productItems, normalizedId]
   );

   const totalSelected = context.productItems.length;

   const isDisabled = useMemo(
      () => !isChecked && totalSelected >= 5,
      [isChecked, totalSelected]
   );

   return {
      isChecked,
      isDisabled,
      totalSelected,
      isInitialized: context.isInitialized,
      addProduct: context.addProduct,
      removeProduct: context.removeProduct
   };
};
